/* Reference/profile fixtures remain during cutover. Normal builds derive staff
 * access from the server; only the explicit walkthrough build uses personas. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAccess } from '../auth/AccessContext.jsx';
import { staffAccess } from '../auth/staffAccess.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { DEMO_PROFILES, getReference, resetDemo, subscribe } from './store.js';

const PERSONA_KEY = 'ihs-it-help.demo.persona';
const PROFILE_KEY = 'ihs-it-help.demo.profile';

/* Who you are pretending to be, and WHICH DESKS that person works.
 *
 * `desks` is the prototype's stand-in for the Hub rooms. In Phase 2b each id
 * here becomes a room key resolved by the server, `myDesks` becomes the room
 * set from GET /api/access/me, and this list disappears with the demo bar.
 *
 * The three single-desk personas exist for the walkthroughs: Rhea needs to see
 * what a ServiceTitan-only queue looks like without also being handed every
 * broken-laptop ticket at IHS. An employee works no desk at all, which is the
 * normal case — most people only ever raise tickets.
 */
export const PERSONAS = [
  { id: 'employee',     label: 'Employee',       desks: [] },
  { id: 'it',           label: 'IT staff',       desks: ['it-help'] },
  { id: 'servicetitan', label: 'ServiceTitan',   desks: ['servicetitan-help'] },
  { id: 'ai',           label: 'AI staff',       desks: ['ai-access'] },
  { id: 'admin',        label: 'IT admin',       desks: ['it-help', 'servicetitan-help', 'ai-access'] },
];

const DemoContext = createContext(null);

function readStored(key, fallback) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export function DemoProvider({ children }) {
  const { user } = useAuth();
  const { staff, status: accessStatus } = useAccess();
  const liveAccess = useMemo(() => staffAccess(staff, accessStatus), [staff, accessStatus]);
  const walkthrough = import.meta.env.VITE_WALKTHROUGH_DEMO === 'true';
  const [persona, setPersonaState] = useState(() => readStored(PERSONA_KEY, 'employee'));
  const [profileId, setProfileIdState] = useState(() => readStored(PROFILE_KEY, 'dispatcher'));
  const [reference, setReference] = useState(null);
  const [version, setVersion] = useState(0);

  // Any write to the store bumps this, and every screen reading it re-fetches.
  // Crude on purpose — it is the same "something changed, read it again" that a
  // real cache invalidation does, without a library nobody needs yet.
  useEffect(() => subscribe(() => setVersion((v) => v + 1)), []);

  useEffect(() => {
    let cancelled = false;
    getReference().then((r) => {
      if (!cancelled) setReference(r);
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const setPersona = useCallback((id) => {
    setPersonaState(id);
    try {
      localStorage.setItem(PERSONA_KEY, id);
    } catch {
      /* persistence is a nicety here */
    }
  }, []);

  const setProfileId = useCallback((id) => {
    setProfileIdState(id);
    try {
      localStorage.setItem(PROFILE_KEY, id);
    } catch {
      /* persistence is a nicety here */
    }
  }, []);

  const reset = useCallback(async () => {
    await resetDemo();
  }, []);

  // The signed-in person, joined to the demo profile. In the real app the
  // profile half comes from Entra groups + the HR data already in Snowflake,
  // which is what the "who raised it" family of rules reads.
  const me = useMemo(() => {
    const profile = DEMO_PROFILES[profileId] || DEMO_PROFILES.dispatcher;
    return {
      // "there" rather than "You", so the greeting still reads as English when
      // there is no identity — which is the case on a local dev server, where
      // /.auth/me does not exist.
      name: user?.name || 'there',
      firstName: user?.firstName || user?.name || 'there',
      email: user?.email || 'demo@intermountainhs.com',
      department: profile.department,
      role: profile.role,
      brand: profile.brand,
      isLeadership: profile.isLeadership,
      // Reshapes Home into the technician layout. An HR fact, NOT an access
      // fact — see the note at the top of this file.
      isField: Boolean(profile.isField),
      profileLabel: profile.label,
    };
  }, [user, profileId]);

  // Which IT staff row the signed-in person IS, so "Mine" and "Take it" assign
  // to the right human. SWA hands us a UPN rather than a display name, so
  // "GMyers" has to be matched against "Grant Myers". In the real app this is
  // the caller's own oid and there is nothing to guess.
  const meStaffId = useMemo(() => {
    const staff = reference?.staff || [];
    if (!staff.length) return null;
    const tokens = String(me.name || '').toLowerCase().split(/[\s._-]+/).filter(Boolean);
    const match = staff.find((p) => {
      const surname = p.name.toLowerCase().split(' ').pop();
      return tokens.some((t) => t.length > 2 && (p.name.toLowerCase().includes(t) || t.includes(surname)));
    });
    return (match || staff[0]).id;
  }, [reference, me.name]);

  // Empty for an unknown persona, which fails CLOSED — the same way the real
  // room set does while it is loading or after an error. A typo in a persona id
  // must hide the queue, never reveal it.
  const myDesks = useMemo(
    () => walkthrough ? (PERSONAS.find((p) => p.id === persona)?.desks || []) : liveAccess.myDesks,
    [persona, walkthrough, liveAccess]
  );

  const value = useMemo(
    () => ({
      persona,
      setPersona,
      profileId,
      setProfileId,
      // "Do you work ANY desk?" — it decides whether staff surfaces appear at
      // all. Derived from `desks` rather than listed persona ids, so adding a
      // fourth desk persona cannot forget to update it.
      isIT: myDesks.length > 0,
      isAdmin: walkthrough ? persona === 'admin' : liveAccess.isAdmin,
      walkthrough,
      accessLoading: !walkthrough && accessStatus === 'loading',
      // Server-resolved desks in normal builds.
      myDesks,
      isField: me.isField,
      me,
      meStaffId,
      reference,
      version,
      reset,
    }),
    [persona, setPersona, profileId, setProfileId, walkthrough, liveAccess, accessStatus, myDesks, me, meStaffId, reference, version, reset]
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside <DemoProvider>');
  return ctx;
}
